<template>
  <div class="repair-statistics-container">
    <div class="single-statistical-card-container">
      <single-statistical-card v-for="(item, index) in repairTaskWorkOrderStatistics" :key="index" :title="item.name" :count="item.value" />
    </div>
    <div class="statistics-chart-container">
      <div class="statistics-query-container">
        <div class="statistics-query-dimension-container">
          <div
            v-for="(item,index) in timeDimension"
            :key="index"
            class="statistics-query-dimension"
            :class="{'is-selected-dimension': item.name === isRepairSelectedDimension}"
            @click="selectQueryRepairTimeDimension(item.name)"
          >{{ item.value }}</div>
        </div>
        <el-date-picker
          v-model="repairDateRange"
          style="width: 350px"
          :type="getRepairTimeDimension.rangeType"
          range-separator="至"
          :start-placeholder="getRepairTimeDimension.startLabel"
          :end-placeholder="getRepairTimeDimension.endLabel"
          :picker-options="pickerOptions"
          @blur="resetMinDate"
          @change="repairDateRangeChange"
        />
      </div>
      <el-row :gutter="24">
        <el-col :span="12">
          <dw-statistic-chart :is-loading="isRepairCountChartLoading" title="维修次数统计" :box-style="{ 'margin-right': '20px', width: '100%' }" :title-style="{width: '220px'}">
            <template v-slot:chart>
              <repair-count-chart ref="repairCountChart" first-name="维修完成工单数" second-name="设备数量" x-unit="时间" y-unit="数量" height="300px" :color1="variables.primaryColor" />
            </template>
          </dw-statistic-chart>
        </el-col>
        <el-col :span="12">
          <dw-statistic-chart :is-loading="isRepairCountChartLoading" title="维修完成情况统计" :box-style="{ 'margin-right': '20px', width: '100%' }" :title-style="{width: '220px'}">
            <template v-slot:chart>
              <repair-finish-chart ref="repairFinishChart" first-name="已完成工单数" second-name="未完成工单数" x-unit="数量" y-unit="时间" height="300px" />
            </template>
          </dw-statistic-chart>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
import SingleStatisticalCard from '@/views/newDashboard/components/card/singleStatisticalCard'
import RepairCountChart from '@/views/newDashboard/components/chart/repairCountChart'
import RepairFinishChart from '@/views/newDashboard/components/chart/repairFinishChart'
import variables from '@/styles/variables.scss'
import { parseTime } from '@/utils'
import { getTaskWorkOrderBarStatistics, getTaskWorkOrderGridStatistics } from '@/api/newDashboard/dashboard'

export default {
  name: 'RepairStatistics',
  components: {
    SingleStatisticalCard,
    RepairCountChart,
    RepairFinishChart
  },
  data() {
    return {
      pickerOptions: {
        disabledDate: (time) => {
          if (this.minDate !== null && this.maxDate === null) {
            if (this.isRepairSelectedDimension === 'day') {
              const minDate = this.minDate.getDate()
              const lastDate = new Date(this.minDate).setDate(minDate - 30)
              const nextDate = new Date(this.minDate).setDate(minDate + 30)
              // 只能选 minDate 前后30天的范围 + 未来日期禁止选择
              return time.valueOf() < lastDate.valueOf() || time.valueOf() > nextDate.valueOf() || time.getTime() > Date.now()
            } else {
              const minMonth = this.minDate.getMonth()
              const lastYear = new Date(this.minDate).setMonth(minMonth - 11)
              const nextYear = new Date(this.minDate).setMonth(minMonth + 11)
              // 只能选 minDate 前后一年的范围  + 未来日期禁止选择
              return time.valueOf() < lastYear.valueOf() || time.valueOf() > nextYear.valueOf() || time.getTime() > Date.now()
            }
          }
          // 未来日期禁止选择
          return time.getTime() > Date.now()
        },
        onPick: ({ minDate, maxDate }) => {
          this.minDate = minDate
          this.maxDate = maxDate
        }
      },
      minDate: null,
      maxDate: null,
      timeDimension: [
        { name: 'month', value: '按月统计' },
        { name: 'day', value: '按日统计' }
      ],
      timeRange: [
        { name: 'month', rangeType: 'monthrange', startLabel: '开始月份', endLabel: '结束月份' },
        { name: 'day', rangeType: 'daterange', startLabel: '开始日期', endLabel: '结束日期' }
      ],
      // 维修
      repairTaskWorkOrderStatistics: [
        { name: '今日待执行', value: 0 },
        { name: '今日已执行', value: 0 },
        { name: '待执行工单总数', value: 0 },
        { name: '累计完成工单数', value: 0 }
      ],
      repairDateRange: null,
      repairListQuery: null,
      isRepairSelectedDimension: 'day',
      isRepairCountChartLoading: false
    }
  },
  computed: {
    getRepairTimeDimension() {
      const index = this.timeRange.findIndex(item => item.name === this.isRepairSelectedDimension)
      if (index !== -1) {
        return this.timeRange[index]
      } else {
        return this.timeRange[1]
      }
    },
    variables() {
      return variables
    }
  },
  created() {
    this.initDate()
    this.getRepairTaskWorkOrderCount()
    this.getRepairStatisticCount()
  },
  methods: {
    resetMinDate() {
      if (this.patrolDateRange && this.patrolDateRange.length && this.minDate && !this.maxDate) {
        this.minDate = new Date(this.patrolDateRange[0])
      } else {
        this.minDate = null
      }
    },
    initDate(isDate = true) {
      // 月初开始
      const now = new Date()
      const year = now.getFullYear()
      const month = now.getMonth()
      const day = now.getDate()
      let range = []
      if (isDate) {
        const start = new Date(year, month, 1)
        const end = new Date(year, month, day)
        range = [parseTime(start, '{y}-{m}-{d}'), parseTime(end, '{y}-{m}-{d}')]
      } else {
        const start = new Date(year, 0)
        const end = new Date(year, month)
        range = [parseTime(start, '{y}-{m}'), parseTime(end, '{y}-{m}')]
      }
      this.repairListQuery = { startAt: range[0], endAt: range[1] }
      this.repairDateRange = [range[0], range[1]]
    },
    selectQueryRepairTimeDimension(value) {
      this.isRepairSelectedDimension = value
      this.initDate(this.isRepairSelectedDimension === 'day', false, true)
      this.getRepairStatisticCount()
    },
    repairDateRangeChange(value) {
      const range = this.dateRangeChange(value, 'isRepairSelectedDimension')
      this.repairListQuery = { startAt: range[0], endAt: range[1] }
      this.repairDateRange = [range[0], range[1]]
      this.getRepairStatisticCount()
    },
    dateRangeChange(value, type) {
      let range = []
      if (this[type] === 'day') {
        range = [parseTime(value[0], '{y}-{m}-{d}'), parseTime(value[1], '{y}-{m}-{d}')]
      } else {
        range = [parseTime(value[0], '{y}-{m}'), parseTime(value[1], '{y}-{m}')]
      }
      return range
    },
    getRepairTaskWorkOrderCount() {
      const listQuery = {
        type: 'REPAIR'
      }
      getTaskWorkOrderGridStatistics(listQuery).then(resp => {
        this.repairTaskWorkOrderStatistics = resp
      })
    },
    getRepairStatisticCount() {
      const listQuery = this.repairListQuery
      listQuery.timeDimension = this.isRepairSelectedDimension
      listQuery.type = 'REPAIR'
      this.isRepairCountChartLoading = true
      getTaskWorkOrderBarStatistics(listQuery).then(resp => {
        this.isRepairCountChartLoading = false
        this.$nextTick(() => {
          this.$refs.repairCountChart.getData(resp.time, resp.finishedCounts, resp.finishedAbnormalCounts)
          this.$refs.repairFinishChart.getData(resp.time, resp.currentFinishedCounts, resp.currentUnfinishedCounts)
        })
      }).catch(() => {
        this.isRepairCountChartLoading = false
      })
    }
  }
}
</script>

<style scoped lang="scss">
@import '~@/styles/variables.scss';

.work-order-statistics-container{
  .single-statistical-card-container{
    display: flex;
    flex-direction: row;
    gap: 20px;
    margin-bottom: 20px;
  }
  .statistics-chart-container{
    .statistics-query-container{
      display: flex;
      align-items: center;
      justify-content: right;
      .statistics-query-dimension-container{
        display: flex;
        align-items: center;
        justify-content: center;
        .statistics-query-dimension{
          width: 100px;
          height: 28px;
          line-height: 28px;
          text-align: center;
          background: #ffffff;
          color: #606266;
          border: 1px solid #dcdfe6;
          border-radius: 5px;
          margin-right: 10px;
          cursor: pointer;
          font-size: 14px;
        }
        .statistics-query-dimension:hover{
          color: $primaryColor;
          background: $primaryColorLight;
          border-color: $primaryColor;
        }
        .statistics-query-dimension:focus{
          color: $primaryColor;
          background: $primaryColorLight;
          border-color: $primaryColor;
        }
        .is-selected-dimension{
          color: #fff !important;
          background-color: $primaryColor !important;
          border-color: $primaryColor !important;
        }
      }
    }
  }
}
</style>
